import type StoreService from '@ember-data/store';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import { pollWithDeadline, REQUEST_TIMEOUT_MS, withRequestDeadline } from 'garaje/utils/document-uploads';

/**
 * Stateless transport for the admin pre-reg manage flow's DIRECT
 * visitor-document upload, over the `/api/v3/visitor-document-upload-links`
 * action endpoints (envoy-web #21271 + #21273). The sibling of
 * `draft-documents.ts` (the create flow): both depend on the neutral
 * `document-uploads.ts` helpers, never on each other (the VIS-17030 boundary).
 *
 * Where draft-documents reserves an unattached draft for the create form, this
 * flow attaches the document directly to an existing visit — an invite XOR an
 * entry (never both). The visit target travels as a JSON:API relationship in
 * both the request and the confirm, so both share `buildRelationships`.
 *
 * These are plain functions taking `store` (the existing utils idiom — see
 * `draft-documents.ts` / `notify-workflow.ts`): the flow has no shared state.
 * The consuming card sequences request-link → upload → confirm → poll inside
 * its own ember-concurrency task (so each step is a cancellation point) and
 * owns the loading/error UI; this layer never swallows an error or substitutes
 * a silent fallback — a stalled request and a hung poll reject rather than
 * hanging the caller's task forever.
 *
 * `adapter.ajax` with hand-built JSON:API bodies (not an Ember Data
 * model+adapter): the confirm resource (`visitor-document-upload-link-confirmations`)
 * is an action with no stored model, so it must be hand-built — and building
 * the request the same way keeps one contract shape, with the conditional
 * invite-XOR-entry relationship expressed once.
 */

/** Which visit a manage-flow document attaches to. */
export type VisitType = 'invite' | 'entry';

export interface VisitorDocUploadLink {
  documentId: string;
  uploadLink: string;
}

export interface RequestVisitorDocUploadLinkParams {
  visitType: VisitType;
  visitId: string;
  templateId: string;
  contentType: string;
  byteSize: number;
  timeoutMs?: number;
}

export interface ConfirmVisitorDocumentParams {
  visitType: VisitType;
  visitId: string;
  templateId: string;
  timeoutMs?: number;
}

// The relationship KEY is singular (`invite`/`entry`); the resource TYPE is
// plural (`invites`/`entries`), per the verified BE contract.
const VISIT_TARGET_TYPE: Record<VisitType, 'invites' | 'entries'> = {
  invite: 'invites',
  entry: 'entries',
};

interface ResourceIdentifierRelationship {
  data: { type: string; id: string };
}

interface JsonApiResource {
  type: string;
  id: string;
  attributes?: Record<string, unknown>;
  relationships?: Record<string, { data?: { type: string; id: string } | null }>;
}

interface UploadLinkDocument {
  data: {
    type: 'visitor-document-upload-links';
    id: string;
    attributes: { 'upload-link'?: string };
  };
}

interface ConfirmationDocument {
  data: {
    type: 'visitor-document-upload-link-confirmations';
    relationships?: {
      'visitor-document'?: { data?: { type: string; id: string } | null };
    };
  };
  included?: JsonApiResource[];
}

function visitorDocumentAdapter(store: StoreService) {
  return store.adapterFor('visitor-document');
}

/**
 * The template + visit-target relationships shared verbatim by the request and
 * the confirm. `template` is always a `custom-user-document-templates`
 * reference; exactly one of `invite`/`entry` is attached.
 */
function buildRelationships(
  visitType: VisitType,
  visitId: string,
  templateId: string,
): Record<string, ResourceIdentifierRelationship> {
  return {
    template: { data: { type: 'custom-user-document-templates', id: templateId } },
    [visitType]: { data: { type: VISIT_TARGET_TYPE[visitType], id: visitId } },
  };
}

/**
 * Reserve a visitor-document and obtain a write-only S3 upload URL for the file.
 *
 * `content-type` / `byte-size` are attributes; the template and the visit
 * (invite XOR entry) are relationships — the template is NOT an attribute. The
 * response's `data.id` is the document id the confirm step targets, and
 * `attributes.upload-link` is the presigned PUT URL the card uploads to (the
 * card calls the neutral `uploadFile`, not this transport).
 */
export async function requestVisitorDocUploadLink(
  store: StoreService,
  {
    visitType,
    visitId,
    templateId,
    contentType,
    byteSize,
    timeoutMs = REQUEST_TIMEOUT_MS,
  }: RequestVisitorDocUploadLinkParams,
): Promise<VisitorDocUploadLink> {
  const adapter = visitorDocumentAdapter(store);
  const url = adapter.buildURL('visitor-document-upload-link');

  const payload = (await withRequestDeadline(
    adapter.ajax(url, 'POST', {
      data: {
        data: {
          type: 'visitor-document-upload-links',
          attributes: { 'content-type': contentType, 'byte-size': byteSize },
          relationships: buildRelationships(visitType, visitId, templateId),
        },
      },
    }),
    'Visitor document upload-link request',
    timeoutMs,
  )) as UploadLinkDocument;

  const documentId = payload?.data?.id;
  const uploadLink = payload?.data?.attributes?.['upload-link'];
  if (!documentId || !uploadLink) {
    throw new Error('Visitor document upload-link response missing document id or upload-link');
  }

  return { documentId, uploadLink };
}

/**
 * Confirm the uploaded file and begin server-side parsing. The document is
 * auto-approved on the visit (`pre_approved: true`).
 *
 * The confirm response's primary `data` is a
 * `visitor-document-upload-link-confirmations` resource — which has no Ember
 * Data model — that links the created `visitor-documents` resource via its
 * `visitor-document` relationship, with the visitor-document itself (plus its
 * approval link) in `included`. So the payload is re-rooted on that included
 * visitor-document before `pushPayload`, rather than pushing the confirmation
 * resource (which would fail to normalize). The visitor-document id is read
 * from the relationship — never assumed equal to the request's document id.
 */
export async function confirmVisitorDocument(
  store: StoreService,
  documentId: string,
  { visitType, visitId, templateId, timeoutMs = REQUEST_TIMEOUT_MS }: ConfirmVisitorDocumentParams,
): Promise<VisitorDocumentModel> {
  const adapter = visitorDocumentAdapter(store);
  const url = `${adapter.buildURL('visitor-document-upload-link', documentId)}/confirm`;

  const payload = (await withRequestDeadline(
    adapter.ajax(url, 'POST', {
      data: {
        data: {
          type: 'visitor-document-upload-link-confirmations',
          relationships: buildRelationships(visitType, visitId, templateId),
        },
      },
    }),
    'Visitor document confirm',
    timeoutMs,
  )) as ConfirmationDocument;

  const visitorDocumentId = payload?.data?.relationships?.['visitor-document']?.data?.id;
  if (!visitorDocumentId) {
    throw new Error('Visitor document confirm response missing the visitor-document relationship');
  }

  const included = Array.isArray(payload.included) ? payload.included : [];
  const primary = included.find(
    (resource) => resource.type === 'visitor-documents' && resource.id === visitorDocumentId,
  );
  if (!primary) {
    throw new Error('Visitor document confirm response missing the included visitor-document');
  }

  store.pushPayload('visitor-document', {
    data: primary,
    included: included.filter((resource) => resource !== primary),
  });

  const visitorDocument = store.peekRecord('visitor-document', visitorDocumentId);
  if (!visitorDocument) {
    throw new Error(`Visitor document ${visitorDocumentId} missing from store after confirm`);
  }

  return visitorDocument;
}

/**
 * Single GET against the visitor-document (always reloads from the server). The
 * card reads `processing-status` / dynamic `field-values` off the returned
 * record to decide whether to keep polling.
 */
export async function pollVisitorDocument(store: StoreService, documentId: string): Promise<VisitorDocumentModel> {
  return store.findRecord('visitor-document', documentId, { reload: true });
}

/**
 * A single poll bounded by `deadlineMs`, delegating to the neutral
 * `pollWithDeadline` racer — Ember Data's adapter has no transport-level
 * timeout, so a hung GET would otherwise let the caller's top-of-loop deadline
 * check be skipped, hanging the poll loop forever.
 */
export function pollVisitorDocumentWithDeadline(
  store: StoreService,
  documentId: string,
  deadlineMs: number,
): Promise<VisitorDocumentModel> {
  return pollWithDeadline(
    () => pollVisitorDocument(store, documentId),
    `Visitor document ${documentId} parsing`,
    deadlineMs,
  );
}
